import React, { useCallback } from 'react';

import { styled } from '@compiled/react';
import isNil from 'lodash/isNil';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { CSV, type ImportType } from '../../../common/constants/import-type';
import { LearnMore } from '../../../common/ui';
import { FileBrowser, FileDropTarget } from '../../../common/ui/file-dropzone';
import { DropzoneContent } from '../../../common/ui/file-dropzone/dropzone-content';
import { useProjectSpecificContent } from '../../../common/utils/project-specific-content';
import { useProjectData } from '../../../controllers/import-state';
import { useSource } from '../../../controllers/import-state/selectors/source';
import { useCSVDropzone } from '../../../controllers/utils/use-csv-dropzone';
import { UploadAdvancedOptions, UploadAdvancedOptionsNew } from './upload-advanced-options';

type Props = {
	onNext: (importType?: ImportType) => void;
};

export const UploadCSVDropZone = ({ onNext }: Props) => {
	const { formatMessage } = useIntl();

	const onContinue = useCallback(() => {
		onNext(CSV);
	}, [onNext]);
	const [source] = useSource();

	const [{ type }] = useProjectData();
	const {
		uploadCsvDropzoneHeading,
		uploadCsvDropzoneLearnMore,
		uploadFooterContinue,
		uploadLearnMoreLink,
	} = useProjectSpecificContent(type, source);

	const {
		isFileBrowserOpen,
		isFileNotCSV,
		isFileTooLarge,
		isFileUploading,
		hasErrorOccurred,
		uploadedFileName,
		onReceiveFile,
		onRemoveFile,
		onCloseFileBrowser,
		onOpenFileBrowser,
		onFileDraggedAndDropped,
	} = useCSVDropzone();

	return (
		<Wrapper>
			<Header>
				<Heading size="medium" as="h2">
					{formatMessage(uploadCsvDropzoneHeading)}
				</Heading>
				<LearnMore href={uploadLearnMoreLink} target="_blank">
					{formatMessage(uploadCsvDropzoneLearnMore)}
				</LearnMore>
			</Header>
			<FileDropTarget
				onReceiveFile={onFileDraggedAndDropped}
				onClick={onOpenFileBrowser}
				isDisabled={isFileUploading}
			>
				<DropzoneContent
					isFileUploading={isFileUploading}
					isFileNotCSV={isFileNotCSV}
					hasErrorOccurred={hasErrorOccurred}
					isFileTooLarge={isFileTooLarge}
					uploadedFileName={uploadedFileName}
					onRemoveFile={onRemoveFile}
				/>
			</FileDropTarget>
			<FileBrowser
				isOpen={isFileBrowserOpen}
				onReceiveFile={onReceiveFile}
				onClose={onCloseFileBrowser}
			/>
			{fg('jwm-x2c-entry-point-improvement') ? (
				<UploadAdvancedOptionsNew />
			) : (
				<UploadAdvancedOptions />
			)}
			{!isNil(uploadedFileName) && !isFileUploading && (
				<NextButton
					appearance="primary"
					onClick={onContinue}
					testId="project-create-importer-upload-flow.ui.upload-screen.upload-csv-dropzone.next-button"
				>
					{formatMessage(uploadFooterContinue)}
				</NextButton>
			)}
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NextButton = styled(Button)({
	marginTop: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	padding: token('space.400', '32px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	marginTop: token('space.400', '32px'),
	marginBottom: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	flexWrap: 'wrap',
	alignItems: 'baseline',
});
